import { useState } from "react";
import { Button, Container, Form, Spinner, Table } from "react-bootstrap";
import { Title } from "../components/common/Title";
import { useApiResource } from "../hooks/useApiResource";
import type { FernQuery } from "../types/fernQueries";
import "./FernLog.css";

const API_HOST = process.env.REACT_APP_API;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const CATEGORIES = ["All", "Aard", "General", "Discord", "Follow-up"] as const;

// Unlinked page (no nav entry): the latest 100 queries Fern answered, or the
// latest 100 of one category so the rarer ones aren't pushed out.
export const FernLog = () => {
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("All");
  const { data, loading, error, reload } = useApiResource<FernQuery[]>(
    `${API_HOST}public/fern-queries${
      category === "All" ? "" : `?category=${encodeURIComponent(category)}`
    }`,
    `fern-log-${category}`,
  );

  return (
    <Container fluid>
      <div className="d-flex align-items-center justify-content-between">
        <Title
          text={`Fern Log - Latest 100 ${category === "All" ? "" : `${category} `}Queries`}
        />
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as (typeof CATEGORIES)[number])
            }
            aria-label="Filter by type"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
          <Button
            variant="outline-light"
            size="sm"
            onClick={reload}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data && <Spinner animation="border" variant="warning" />}
      {error && <div className="text-danger">{error.message}</div>}

      {data && (
        <Table
          bordered
          hover
          variant="dark"
          size="sm"
          responsive
          className="fern-log"
        >
          <thead>
            <tr>
              <th>Time</th>
              <th>Sender</th>
              <th>Type</th>
              <th>Query</th>
              <th>Response</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row.id}
                className={`fern-row-${row.category.toLowerCase().replace(/\W+/g, "-")}${
                  row.success ? "" : " fern-row-failed"
                }`}
              >
                <td className="text-nowrap">{formatTime(row.createdAt)}</td>
                <td>{row.sender}</td>
                <td>
                  <span className="fern-category">{row.category}</span>
                </td>
                <td>{row.query}</td>
                <td>{row.response}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default FernLog;
